import type { AuthenticatedAdmin } from "@utopia-well-done-ltd/contracts";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ChatDetail, ChatMessage } from "../api/chat.js";
import { ApiError, api } from "../api/client.js";
import { can } from "../capabilities.js";
import { useLanguage } from "../i18n.js";
import "./chat-moderation.css";

type Choice = "clear" | "violation" | "mute" | "unmute" | "remove";
export function ChatModeration({ admin }: { admin: AuthenticatedAdmin }) {
  const french = useLanguage() === "fr";
  const text = (en: string, fr: string) => (french ? fr : en);
  const allowed = can(admin.role, "players.status.write");
  const [queue, setQueue] = useState<ChatMessage[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<ChatDetail | null>(null);
  const [revision, setRevision] = useState(0);
  const [tab, setTab] = useState("conversation");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [denied, setDenied] = useState(false);
  const [choice, setChoice] = useState<Choice | null>(null);
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState("harassment");
  const [minutes, setMinutes] = useState(10);
  const [busy, setBusy] = useState(false);
  const [receipt, setReceipt] = useState(false);
  const retry = useRef<{ signature: string; id: string } | null>(null);
  const saving = useRef(false);
  const lifetime = useRef(new AbortController());
  useEffect(() => {
    lifetime.current = new AbortController();
    return () => lifetime.current.abort();
  }, []);
  const reject = useCallback((e: unknown) => {
    if (
      e instanceof ApiError &&
      (e.status === 403 || (e.status === 401 && e.code !== "AUTH.REAUTH_REQUIRED"))
    ) {
      setDenied(true);
      setQueue([]);
      setDetail(null);
    }
    setError(e instanceof Error ? e.message : "Request failed.");
  }, []);
  useEffect(() => {
    if (!allowed || denied) return;
    void revision;
    const controller = new AbortController();
    let pending = false;
    const load = async () => {
      if (pending || document.visibilityState === "hidden") return;
      pending = true;
      try {
        const r = await api.chatQueue(controller.signal);
        if (!controller.signal.aborted) {
          setQueue(r.messages);
          setLoading(false);
        }
      } catch (e) {
        if (!controller.signal.aborted) {
          reject(e);
          setLoading(false);
        }
      } finally {
        pending = false;
      }
    };
    void load();
    const timer = setInterval(load, 10000);
    return () => {
      controller.abort();
      clearInterval(timer);
    };
  }, [allowed, denied, revision, reject]);
  useEffect(() => {
    if (!selected || !allowed || denied) return;
    void revision;
    const controller = new AbortController();
    setDetail(null);
    setChoice(null);
    setError("");
    api
      .chatDetail(selected, controller.signal)
      .then((r) => {
        if (!controller.signal.aborted) setDetail(r);
      })
      .catch((e) => {
        if (!controller.signal.aborted) reject(e);
      });
    return () => controller.abort();
  }, [selected, revision, allowed, denied, reject]);
  const label = (c: Choice) =>
    ({
      clear: text("Clear", "Valider"),
      violation: text("Violation", "Infraction"),
      mute: text("Mute player", "Rendre muet"),
      unmute: text("Unmute player", "Rétablir le chat"),
      remove: text("Remove message", "Supprimer le message"),
    })[c];
  const choose = (c: Choice) => {
    setChoice(c);
    setReceipt(false);
    setReason(
      c === "clear"
        ? text("No violation in this context.", "Aucune infraction dans ce contexte.")
        : "",
    );
  };
  const save = async () => {
    if (!detail || !choice || reason.trim().length < 3 || saving.current) return;
    saving.current = true;
    setBusy(true);
    setError("");
    const signature = JSON.stringify([
      detail.message.id,
      detail.message.reviewVersion,
      choice,
      reason,
      category,
      minutes,
    ]);
    if (retry.current?.signature !== signature)
      retry.current = { signature, id: crypto.randomUUID() };
    try {
      if (choice === "clear" || choice === "violation")
        await api.chatDecision(
          detail.message.id,
          {
            requestId: retry.current.id,
            version: detail.message.reviewVersion,
            verdict: choice,
            category: choice === "clear" ? "none" : category,
            reason: reason.trim(),
          },
          lifetime.current.signal,
        );
      else
        await api.chatAction(
          detail.message.id,
          {
            requestId: retry.current.id,
            action: choice,
            reason: reason.trim(),
            ...(choice === "mute" ? { minutes } : {}),
          },
          lifetime.current.signal,
        );
      if (!lifetime.current.signal.aborted) {
        retry.current = null;
        setReceipt(true);
        setRevision((n) => n + 1);
      }
    } catch (e) {
      if (!lifetime.current.signal.aborted) reject(e);
    } finally {
      saving.current = false;
      if (!lifetime.current.signal.aborted) setBusy(false);
    }
  };
  const date = (value: string | number) =>
    new Date(value).toLocaleString(french ? "fr-FR" : "en-GB", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  if (!allowed || denied)
    return (
      <p className="banner" role="alert">
        {text("Chat moderation access is unavailable.", "Accès à la modération indisponible.")}
      </p>
    );
  return (
    <div className="bo-chat">
      <aside className="bo-chat-feed" aria-label={text("Moderation feed", "Messages à examiner")}>
        <header>
          <strong>
            {text("Needs attention", "À examiner")}{" "}
            <span className="dim">{queue.length === 50 ? "50+" : queue.length}</span>
          </strong>
          <button
            className="btn btn-quiet btn-sm"
            type="button"
            disabled={busy}
            onClick={() => setRevision((n) => n + 1)}
          >
            {text("Refresh", "Actualiser")}
          </button>
        </header>
        {loading ? (
          <p>{text("Loading…", "Chargement…")}</p>
        ) : !queue.length ? (
          <p className="dim">{text("All caught up.", "Tout est à jour.")}</p>
        ) : (
          queue.map((m) => (
            <button
              type="button"
              className={`bo-chat-entry ${m.modelVerdict}`}
              key={m.id}
              disabled={busy}
              aria-pressed={selected === m.id}
              onClick={() => {
                setSelected(m.id);
                setTab("conversation");
                setReceipt(false);
              }}
            >
              <span>
                <strong>{m.name}</strong>
                <small>{date(m.createdAt)}</small>
              </span>
              <b>
                {m.modelVerdict === "block"
                  ? text("Blocked", "Bloqué")
                  : text("Review", "À examiner")}
              </b>
              <p>{m.message}</p>
            </button>
          ))
        )}
      </aside>
      <section className="bo-chat-detail">
        {detail ? (
          <>
            <header>
              <strong>{detail.message.name}</strong>
              <small>{date(detail.message.createdAt)}</small>
            </header>
            <p className={`bo-chat-meta ${detail.message.modelVerdict}`}>
              <b>
                {detail.message.modelVerdict === "block"
                  ? text("Blocked", "Bloqué")
                  : detail.message.modelVerdict === "review"
                    ? text("Review", "À examiner")
                    : text("Allowed", "Autorisé")}
              </b>{" "}
              ·{" "}
              {detail.message.removedAt
                ? text("Removed", "Supprimé")
                : detail.message.modelVerdict === "block"
                  ? text("Not published", "Non publié")
                  : text("Published", "Publié")}
            </p>
            {detail.mutedUntil ? (
              <p className="banner">
                {text("Muted until", "Muet jusqu’au")} {date(detail.mutedUntil)}
              </p>
            ) : null}
            <nav
              className="bo-chat-tabs"
              aria-label={text("Message evidence", "Contexte du message")}
            >
              {["conversation", "history", "actions"].map((t) => (
                <button type="button" key={t} aria-pressed={tab === t} onClick={() => setTab(t)}>
                  {t === "conversation"
                    ? text("Conversation", "Conversation")
                    : t === "history"
                      ? text("Player history", "Historique du joueur")
                      : text("Decisions & actions", "Décisions et actions")}
                </button>
              ))}
            </nav>
            <small className="bo-chat-order">
              {text("Oldest → newest ↓", "Ancien → récent ↓")}
            </small>
            <div className="bo-chat-evidence">
              {tab === "conversation" ? (
                <>
                  {detail.message.context.omitted ? (
                    <small>
                      {text("Earlier messages omitted:", "Messages antérieurs omis :")}{" "}
                      {detail.message.context.omitted}
                    </small>
                  ) : null}
                  {[...detail.message.context.messages]
                    .sort((a, b) => a.sentAt - b.sentAt)
                    .map((m) => (
                      <article key={m.id}>
                        <small>
                          {m.name} · {date(m.sentAt)}
                        </small>
                        <p>{m.text}</p>
                      </article>
                    ))}
                  <article className={`selected ${detail.message.modelVerdict}`}>
                    <small>
                      {text("Selected message", "Message sélectionné")} ·{" "}
                      {date(detail.message.createdAt)}
                    </small>
                    <p>{detail.message.message}</p>
                  </article>
                </>
              ) : null}
              {tab === "history"
                ? [...detail.history]
                    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
                    .map((m) => (
                      <button
                        type="button"
                        disabled={busy}
                        className="bo-chat-history"
                        key={m.id}
                        onClick={() => setSelected(m.id)}
                      >
                        <small>{date(m.createdAt)}</small>
                        <p>{m.message}</p>
                      </button>
                    ))
                : null}
              {tab === "actions" ? (
                <>
                  {[
                    ...detail.decisions.map((d) => ({
                      ...d,
                      title: label(d.verdict === "clear" ? "clear" : "violation"),
                    })),
                    ...detail.punishments.map((p) => ({ ...p, title: label(p.action as Choice) })),
                  ]
                    .sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt))
                    .map((d) => (
                      <article key={d.id}>
                        <small>
                          {date(d.createdAt)} · {d.title}
                        </small>
                        <p>{d.reason}</p>
                        <small>
                          {d.adminModeratorId
                            ? text("Operator", "Opérateur")
                            : text("Moderator", "Modérateur")}{" "}
                          · {d.adminModeratorId ?? d.moderatorId}
                        </small>
                      </article>
                    ))}
                  {!detail.decisions.length && !detail.punishments.length ? (
                    <p className="dim">
                      {text(
                        "No previous decisions or sanctions.",
                        "Aucune décision ou sanction antérieure.",
                      )}
                    </p>
                  ) : null}
                </>
              ) : null}
            </div>
            <footer>
              <button
                className="btn bo-chat-clear"
                type="button"
                disabled={busy}
                onClick={() => choose("clear")}
              >
                {label("clear")}
              </button>
              <button
                className="btn bo-chat-violation"
                type="button"
                disabled={busy}
                onClick={() => choose("violation")}
              >
                {label("violation")}
              </button>
              <select
                aria-label={text("Player action", "Action sur le joueur")}
                disabled={busy}
                value=""
                onChange={(e) => choose(e.target.value as Choice)}
              >
                <option value="" disabled>
                  {text("Actions…", "Actions…")}
                </option>
                {(["remove", "mute", "unmute"] as const).map((c) => (
                  <option key={c} value={c}>
                    {label(c)}
                  </option>
                ))}
              </select>
            </footer>
            {choice ? (
              <form
                className="bo-chat-confirm"
                onSubmit={(e) => {
                  e.preventDefault();
                  void save();
                }}
              >
                <strong>{label(choice)}</strong>
                {choice === "violation" ? (
                  <select
                    aria-label={text("Category", "Catégorie")}
                    value={category}
                    disabled={busy}
                    onChange={(e) => setCategory(e.target.value)}
                  >
                    {["harassment", "hate", "threat", "scam", "privacy", "spam", "other"].map(
                      (c, i) => (
                        <option key={c} value={c}>
                          {french
                            ? [
                                "Harcèlement",
                                "Haine",
                                "Menace",
                                "Arnaque",
                                "Vie privée",
                                "Spam",
                                "Autre",
                              ][i]
                            : c}
                        </option>
                      ),
                    )}
                  </select>
                ) : null}
                {choice === "mute" ? (
                  <select
                    aria-label={text("Mute duration", "Durée")}
                    value={minutes}
                    disabled={busy}
                    onChange={(e) => setMinutes(Number(e.target.value))}
                  >
                    <option value={10}>10 min</option>
                    <option value={60}>1 h</option>
                    <option value={1440}>24 h</option>
                  </select>
                ) : null}
                <label className="field">
                  <span>
                    {choice === "mute"
                      ? text("Reason shown to player", "Motif visible par le joueur")
                      : text("Reason", "Motif")}
                  </span>
                  <textarea
                    value={reason}
                    maxLength={500}
                    disabled={busy}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </label>
                <div>
                  <button
                    className="btn btn-primary"
                    disabled={busy || reason.trim().length < 3}
                    type="submit"
                  >
                    {busy ? text("Saving…", "Enregistrement…") : text("Confirm", "Confirmer")}
                  </button>
                  <button
                    className="btn btn-quiet"
                    type="button"
                    disabled={busy}
                    onClick={() => setChoice(null)}
                  >
                    {text("Cancel", "Annuler")}
                  </button>
                </div>
              </form>
            ) : null}
          </>
        ) : (
          <p className="dim">
            {selected
              ? text("Loading message…", "Chargement du message…")
              : text("Select a message.", "Sélectionnez un message.")}
          </p>
        )}
        {receipt ? <p role="status">{text("Saved.", "Enregistré.")}</p> : null}
        {error ? (
          <p className="banner" role="alert">
            {error}
          </p>
        ) : null}
      </section>
    </div>
  );
}
